import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const WAREHOUSE_IMG = "https://images.pexels.com/photos/4483865/pexels-photo-4483865.jpeg";

const reasons = [
  { k: "Owner-Operator First", v: "We work for the trucks, not the brokers. Your truck, your rules, your RPM target." },
  { k: "Higher Net Revenue", v: "Better rate negotiation + backhauls prepped = more net in your pocket, even after our fee." },
  { k: "One Point of Contact", v: "Same dispatcher every load. They know your truck, your home time, your factoring setup." },
  { k: "We Chase the Money", v: "We invoice, follow up, and handle broker disputes so your factor gets paid faster." },
];

export default function WhyUs() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <section id="why" ref={ref} className="bg-[#F8F9FA] py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 relative">
          <div className="relative w-full h-[520px] overflow-hidden">
            <motion.img src={WAREHOUSE_IMG} alt="Dispatcher operations desk" className="absolute inset-0 w-full h-[120%] object-cover" style={{ y: imgY }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F8F9FA] via-transparent to-transparent opacity-30" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="absolute -bottom-6 -right-6 bg-black text-white p-6 max-w-[260px] hidden md:block">
            <p className="font-display font-bold text-4xl text-[#FF4500]">$0</p>
            <p className="font-mono-label text-[10px] mt-2 text-white/70">SETUP FEES. YOU ONLY PAY WHEN YOU GET PAID.</p>
          </motion.div>
        </div>

        <div className="md:col-span-7">
          <Reveal>
            <p className="font-mono-label text-xs text-[#FF4500]" data-testid="why-eyebrow">CHAPTER 03 / WHY ADTRAX</p>
            <h2 className="font-display font-bold text-4xl md:text-6xl mt-4 leading-[1]" data-testid="why-headline">
              We work for<br/>the truck.<br/><span className="text-[#FF4500] italic">Not the broker.</span>
            </h2>
            <p className="mt-6 text-[#495057] text-lg max-w-xl leading-relaxed">
              Every negotiation, every route, every follow-up is done with your P&amp;L in mind — because when you make more, we make more.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {reasons.map((r, i) => (
              <motion.div key={r.k}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.1 }}
                className="bg-white p-6 group hover:bg-[#F1F3F5] transition-colors" data-testid={`why-item-${r.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <CheckCircle2 size={20} className="text-[#FF4500] group-hover:scale-110 transition-transform" strokeWidth={2}/>
                <h4 className="font-display font-semibold text-lg mt-4">{r.k}</h4>
                <p className="text-sm text-[#495057] mt-2 leading-relaxed">{r.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
